/**
 * Storage key for notification sound preference
 */
const NOTIFICATION_SOUND_ENABLED_KEY = 'absolvire-notification-sound-enabled';

/**
 * Storage key for tracking the last active tab
 */
const ACTIVE_TAB_KEY = 'absolvire-active-tab';
const SOUND_LEADER_TIMEOUT = 5000; // 5 seconds - leader expires if tab is inactive

/**
 * Unique tab ID for this instance
 */
const TAB_ID = Math.random().toString(36).substring(2, 15);

/**
 * BroadcastChannel for coordinating between tabs
 */
let broadcastChannel: BroadcastChannel | null = null;

/**
 * Callbacks for sound preference change listeners
 */
const soundPreferenceChangeCallbacks = new Set<(enabled: boolean) => void>();

/**
 * Initialize BroadcastChannel for cross-tab communication
 */
const initBroadcastChannel = () => {
  if (typeof window === 'undefined' || !window.BroadcastChannel) {
    return null;
  }

  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel('absolvire-notification-sound');

    // Listen for messages from other tabs
    broadcastChannel.onmessage = (event) => {
      if (event.data.type === 'play-sound-request') {
        // If we're the leader, play the sound
        if (isSoundLeader()) {
          playSoundInternal();
        }
      } else if (event.data.type === 'sound-preference-changed') {
        // Sound preference changed in another tab, notify callbacks
        const { enabled } = event.data;
        soundPreferenceChangeCallbacks.forEach((callback) => {
          callback(enabled);
        });
      }
    };
  }

  return broadcastChannel;
};

/**
 * Get the last active tab info
 */
interface ActiveTabInfo {
  tabId: string;
  timestamp: number;
}

const getLastActiveTab = (): ActiveTabInfo | null => {
  try {
    const data = localStorage.getItem(ACTIVE_TAB_KEY);
    if (!data) {
      return null;
    }
    return JSON.parse(data);
  } catch {
    return null;
  }
};

/**
 * Update this tab as the last active tab
 */
const updateActiveTab = () => {
  try {
    const activeTabInfo: ActiveTabInfo = {
      tabId: TAB_ID,
      timestamp: Date.now(),
    };
    localStorage.setItem(ACTIVE_TAB_KEY, JSON.stringify(activeTabInfo));
  } catch (error) {
    console.debug('Could not update active tab:', error);
  }
};

/**
 * Check if this tab is the sound leader (last active tab)
 */
const isSoundLeader = (): boolean => {
  try {
    const activeTab = getLastActiveTab();

    // No active tab recorded, we can become the leader
    if (!activeTab) {
      return true;
    }

    const now = Date.now();

    // If the active tab info is too old (expired), we can become the leader
    if (now - activeTab.timestamp > SOUND_LEADER_TIMEOUT) {
      return true;
    }

    // Check if we are the last active tab
    return activeTab.tabId === TAB_ID;
  } catch (error) {
    return true; // On error, allow this tab to play
  }
};

/**
 * Heartbeat interval for maintaining leadership
 */
let heartbeatInterval: number | null = null;

/**
 * Claim the sound leader role for this tab by updating it as the last active tab
 */
const claimSoundLeader = () => {
  try {
    // Update this tab as the last active tab
    updateActiveTab();

    // Clear existing heartbeat if any
    if (heartbeatInterval !== null) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }

    // Set up a heartbeat to maintain active status while tab is visible
    if (document.visibilityState === 'visible') {
      heartbeatInterval = window.setInterval(() => {
        if (document.visibilityState === 'visible') {
          // Only update if we're still the active tab (check before updating)
          const activeTab = getLastActiveTab();
          if (!activeTab || activeTab.tabId === TAB_ID) {
            updateActiveTab();
          }
        } else {
          // Tab is no longer visible, stop heartbeat
          if (heartbeatInterval !== null) {
            clearInterval(heartbeatInterval);
            heartbeatInterval = null;
          }
        }
      }, 1000); // Update every 1 second to be more responsive
    }
  } catch (error) {
    console.debug('Could not claim sound leader:', error);
  }
};

/**
 * Get notification sound enabled state from localStorage
 * Defaults to true if not set
 */
export const getNotificationSoundEnabled = (): boolean => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_SOUND_ENABLED_KEY);
    return stored !== 'false'; // Default to true if not set
  } catch (error) {
    return true; // Default to true on error
  }
};

/**
 * Set notification sound enabled state in localStorage and broadcast to all tabs
 */
export const setNotificationSoundEnabled = (enabled: boolean): void => {
  try {
    localStorage.setItem(NOTIFICATION_SOUND_ENABLED_KEY, String(enabled));

    // Broadcast the change to all tabs via BroadcastChannel
    if (broadcastChannel) {
      broadcastChannel.postMessage({
        type: 'sound-preference-changed',
        enabled,
        tabId: TAB_ID,
      });
    }

    // Also notify local callbacks
    soundPreferenceChangeCallbacks.forEach((callback) => {
      callback(enabled);
    });
  } catch (error) {
    console.debug('Could not save notification sound preference:', error);
  }
};

/**
 * Subscribe to sound preference changes (for UI components that need to update)
 * Returns a cleanup function to unsubscribe
 */
export const onSoundPreferenceChange = (
  callback: (enabled: boolean) => void
): (() => void) => {
  soundPreferenceChangeCallbacks.add(callback);

  // Return cleanup function
  return () => {
    soundPreferenceChangeCallbacks.delete(callback);
  };
};

/**
 * Listen for localStorage changes (for when user changes preference in another tab)
 * This handles cases where BroadcastChannel might not be available
 */
const setupLocalStorageListener = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('storage', (event) => {
    if (event.key === NOTIFICATION_SOUND_ENABLED_KEY) {
      const enabled = event.newValue !== 'false';
      soundPreferenceChangeCallbacks.forEach((callback) => {
        callback(enabled);
      });
    }
  });
};

/**
 * Initialize sound leader coordination
 * Should be called when the app loads
 */
export const initializeSoundLeader = () => {
  // Initialize broadcast channel
  initBroadcastChannel();

  // Setup localStorage listener for cross-tab preference changes
  setupLocalStorageListener();

  // Listen for when this tab becomes active
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // Tab became visible, claim leadership
      claimSoundLeader();
    }
  };

  // Claim leadership on window focus (user switched to this tab)
  const handleFocus = () => {
    claimSoundLeader();
  };

  // Claim leadership on mouse move (user is interacting with this tab)
  const handleMouseMove = () => {
    if (document.visibilityState === 'visible') {
      claimSoundLeader();
    }
  };

  // Claim leadership on any user interaction (click, keypress, etc.)
  const handleUserInteraction = () => {
    if (document.visibilityState === 'visible') {
      claimSoundLeader();
    }
  };

  // Initial claim
  claimSoundLeader();

  // Listen for visibility changes
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Listen for window focus (when user switches to this tab)
  window.addEventListener('focus', handleFocus);

  // Listen for mouse movement (indicates user is active in this tab)
  // Throttle to avoid excessive updates
  let mouseMoveTimeout: number | null = null;
  document.addEventListener('mousemove', () => {
    if (mouseMoveTimeout !== null) {
      clearTimeout(mouseMoveTimeout);
    }
    mouseMoveTimeout = window.setTimeout(() => {
      handleMouseMove();
    }, 500); // Update every 500ms max
  });

  // Listen for user interactions
  document.addEventListener('click', handleUserInteraction);
  document.addEventListener('keydown', handleUserInteraction);

  // Clean up on page unload
  window.addEventListener('beforeunload', () => {
    if (heartbeatInterval !== null) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
    if (mouseMoveTimeout !== null) {
      clearTimeout(mouseMoveTimeout);
      mouseMoveTimeout = null;
    }
    if (broadcastChannel) {
      broadcastChannel.close();
      broadcastChannel = null;
    }
  });

  // Cleanup function
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('focus', handleFocus);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('click', handleUserInteraction);
    document.removeEventListener('keydown', handleUserInteraction);
    if (heartbeatInterval !== null) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
    if (mouseMoveTimeout !== null) {
      clearTimeout(mouseMoveTimeout);
      mouseMoveTimeout = null;
    }
    if (broadcastChannel) {
      broadcastChannel.close();
      broadcastChannel = null;
    }
  };
};

/**
 * Singleton audio context to avoid creating new contexts each time
 */
let audioContext: AudioContext | null = null;

/**
 * Gets or creates the audio context, resuming it if suspended
 */
const getAudioContext = async (): Promise<AudioContext | null> => {
  try {
    if (!audioContext) {
      audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    }

    // Resume audio context if it's suspended (browsers suspend it until user interaction)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    return audioContext;
  } catch (error) {
    console.debug('Could not get audio context:', error);
    return null;
  }
};

/**
 * Internal function to actually play the sound
 */
const playSoundInternal = async () => {
  try {
    const ctx = await getAudioContext();
    if (!ctx) {
      return;
    }

    const now = ctx.currentTime;

    // Helper function to create a tone
    const playTone = (
      frequency: number,
      startTime: number,
      duration: number,
      volume: number
    ) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      // Volume envelope with fade in/out
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };

    // First tone: "dong" - lower pitch
    playTone(600, now, 0.15, 0.3);

    // Second tone: "ding" - higher pitch, slightly delayed
    playTone(800, now + 0.2, 0.2, 0.3);
  } catch (error) {
    // Silently fail if audio context is not available (e.g., in some browsers or when autoplay is blocked)
    console.debug('Could not play notification sound:', error);
  }
};

/**
 * Plays a "dong ding" notification sound when a new notification appears
 * Only plays on the active tab (sound leader) to avoid multiple tabs playing simultaneously
 */
export const playNotificationSound = async () => {
  // Check if sound is enabled
  if (!getNotificationSoundEnabled()) {
    return;
  }

  // Initialize broadcast channel if not already done
  if (!broadcastChannel) {
    initBroadcastChannel();
  }

  // Check if this tab is the sound leader
  if (isSoundLeader()) {
    // We're the leader, play the sound directly
    await playSoundInternal();
  } else {
    // We're not the leader, request the leader to play the sound
    if (broadcastChannel) {
      broadcastChannel.postMessage({
        type: 'play-sound-request',
        tabId: TAB_ID,
      });
    } else {
      // Fallback: if BroadcastChannel is not available, try to play anyway
      await playSoundInternal();
    }
  }
};
